import React, { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import AudioButton from "../AudioButton";
import Confetti from "../Confetti";
import { speak, speakWord, preload } from "../../services/phonicsAudio";
import { sfx } from "../../services/soundfx";

const BUBBLE_COLORS = ["#38BDF8", "#FF6B6B", "#8B5CF6", "#22C55E", "#EC4899", "#F59E0B"];
const ROUNDS = 6;

function pickDistractors(words, target, n) {
  const pool = words.filter((w) => w !== target);
  const out = [];
  while (out.length < n && pool.length) {
    const i = Math.floor(Math.random() * pool.length);
    out.push(pool.splice(i, 1)[0]);
  }
  return out;
}

export default function BubblePopGame({ level, onFinish }) {
  const words = level.words;
  const [round, setRound] = useState(0);
  const [hits, setHits] = useState(0);
  const [misses, setMisses] = useState(0);
  const [burst, setBurst] = useState(false);
  const [popped, setPopped] = useState(null);

  const target = words[round % words.length];

  const bubbles = useMemo(() => {
    const set = [target, ...pickDistractors(words, target, 4)];
    const shuffled = set.sort(() => Math.random() - 0.5);
    return shuffled.map((w, i) => ({
      id: `${round}-${i}-${w}`,
      word: w,
      left: 8 + i * 18 + Math.random() * 6,
      dur: 7 + Math.random() * 4,
      delay: Math.random() * 2,
      color: BUBBLE_COLORS[i % BUBBLE_COLORS.length],
      sway: 10 + Math.random() * 20,
    }));
  }, [round]); // eslint-disable-line

  const callOut = useCallback(() => {
    speak("Pop the word");
    setTimeout(() => speakWord(target), 900);
  }, [target]);

  useEffect(() => {
    preload(words.map((w) => ({ text: w, kind: "word" })));
  }, []); // eslint-disable-line

  useEffect(() => {
    const t = setTimeout(callOut, 500);
    return () => clearTimeout(t);
  }, [round]); // eslint-disable-line

  const pop = (b) => {
    if (popped) return;
    if (b.word === target) {
      sfx.pop();
      setPopped(b.id);
      setHits((h) => h + 1);
      setBurst(true);
      speak("Pop! Yes!");
      setTimeout(() => setBurst(false), 1000);
      setTimeout(() => {
        setPopped(null);
        if (round + 1 >= ROUNDS) {
          const total = ROUNDS;
          const stars = misses === 0 ? 3 : misses <= 2 ? 2 : 1;
          onFinish({ stars, accuracy: (hits + 1) / (hits + 1 + misses) });
        } else {
          setRound((r) => r + 1);
        }
      }, 1100);
    } else {
      sfx.wrong();
      setMisses((m) => m + 1);
      speak("Not that one. Listen again!");
      setTimeout(callOut, 700);
    }
  };

  return (
    <div className="relative flex flex-col items-center px-4 pt-3 min-h-full no-select overflow-hidden" style={{ minHeight: "calc(100dvh - 72px)" }}>
      <Confetti show={burst} count={30} />

      <div className="z-20 flex items-center gap-3 bg-white/95 rounded-3xl border-4 border-sky-300 shadow-lg px-4 py-3 mt-2 max-w-lg">
        <AudioButton text={`Pop the word ${target}`} kind="text" testId="prompt-audio" size={56} />
        <p className="font-display text-lg sm:text-xl font-bold text-sky-900" data-testid="game-prompt">
          Pop the bubble that says <span className="text-pink-600 font-black">"{target}"</span>
        </p>
      </div>

      {/* Bubble field */}
      <div className="absolute inset-0 top-24" data-testid="bubble-field">
        {bubbles.map((b) =>
          popped === b.id ? null : (
            <motion.button
              key={b.id}
              data-testid={`bubble-${b.word}`}
              onClick={() => pop(b)}
              initial={{ y: "100vh" }}
              animate={{ y: "-30vh", x: [0, b.sway, -b.sway, 0] }}
              transition={{
                y: { duration: b.dur, delay: b.delay, repeat: Infinity, ease: "linear" },
                x: { duration: 3, repeat: Infinity, ease: "easeInOut" },
              }}
              whileTap={{ scale: 0.8 }}
              className="pop-btn absolute flex items-center justify-center rounded-full font-display text-2xl font-black text-white shadow-lg"
              style={{
                left: `${b.left}%`,
                width: 92,
                height: 92,
                background: `radial-gradient(circle at 32% 30%, #ffffffcc, ${b.color})`,
                border: "4px solid #ffffff",
                textShadow: "0 2px 4px rgba(0,0,0,0.25)",
              }}
            >
              {b.word}
            </motion.button>
          )
        )}
      </div>

      <div className="z-20 flex gap-2 mt-auto mb-4">
        {Array.from({ length: ROUNDS }).map((_, i) => (
          <div key={i} className={`w-3 h-3 rounded-full ${i < round ? "bg-green-500" : i === round ? "bg-sky-400 animate-pulse" : "bg-slate-300"}`} />
        ))}
      </div>
    </div>
  );
}
